/**
 * Pure-JS SHA-256 (FIPS 180-4). No node:crypto, no Buffer, no TextEncoder:
 * this runs in Node (jest), the browser and Hermes. Output is lowercase hex.
 *
 * Input is a JS string (hashed as UTF-8) or bytes (a Uint8Array, hashed as
 * they are — ruling R15: the phone verifies a downloaded MP3 with this before
 * it plays it). `createSha256()` is the same function fed in slices, so a
 * caller can hash a large file without one long block of work: on Hermes there
 * is no JIT, and a multi-megabyte hash in one call holds the JS thread.
 */
const K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

function utf8Bytes(s: string): Uint8Array {
  const out: number[] = [];
  for (let i = 0; i < s.length; i++) {
    let c = s.charCodeAt(i);
    if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
      const d = s.charCodeAt(i + 1);
      if (d >= 0xdc00 && d <= 0xdfff) {
        c = 0x10000 + ((c - 0xd800) << 10) + (d - 0xdc00);
        i++;
      }
    }
    if (c < 0x80) out.push(c);
    else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 63));
    else if (c < 0x10000) out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
    else out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
  }
  return Uint8Array.from(out);
}

const rotr = (x: number, n: number): number => (x >>> n) | (x << (32 - n));

/** An incremental SHA-256. `update` any number of times, then `digest` once. */
export interface Sha256Hasher {
  update(bytes: Uint8Array): Sha256Hasher;
  /** Lowercase hex. The hasher cannot be used again after this. */
  digest(): string;
}

class Sha256 implements Sha256Hasher {
  private readonly h = new Int32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  private readonly w = new Int32Array(64);
  /** Bytes waiting for a full 64-byte block. */
  private readonly tail = new Uint8Array(64);
  private tailLen = 0;
  /** Total bytes fed so far (exact up to 2^53). */
  private total = 0;
  private done = false;

  update(bytes: Uint8Array): Sha256Hasher {
    if (this.done) throw new Error('sha256: update() after digest()');
    const n = bytes.length;
    this.total += n;
    let i = 0;
    if (this.tailLen > 0) {
      const take = Math.min(64 - this.tailLen, n);
      this.tail.set(bytes.subarray(0, take), this.tailLen);
      this.tailLen += take;
      i = take;
      if (this.tailLen < 64) return this;
      this.block(this.tail, 0);
      this.tailLen = 0;
    }
    for (; i + 64 <= n; i += 64) this.block(bytes, i);
    if (i < n) {
      this.tail.set(bytes.subarray(i), 0);
      this.tailLen = n - i;
    }
    return this;
  }

  digest(): string {
    if (this.done) throw new Error('sha256: digest() called twice');
    // Padding: 0x80, zeros to 56 mod 64, then the bit length as 64-bit big-endian.
    const bitsHi = Math.floor(this.total / 0x20000000) >>> 0; // total * 8 / 2^32
    const bitsLo = ((this.total % 0x20000000) * 8) >>> 0;
    const padLen = this.tailLen < 56 ? 56 - this.tailLen : 120 - this.tailLen;
    const pad = new Uint8Array(padLen + 8);
    pad[0] = 0x80;
    for (const [at, v] of [[padLen, bitsHi], [padLen + 4, bitsLo]] as const) {
      pad[at] = (v >>> 24) & 255;
      pad[at + 1] = (v >>> 16) & 255;
      pad[at + 2] = (v >>> 8) & 255;
      pad[at + 3] = v & 255;
    }
    this.update(pad);
    this.done = true;
    let hex = '';
    for (let i = 0; i < 8; i++) hex += (this.h[i]! >>> 0).toString(16).padStart(8, '0');
    return hex;
  }

  private block(b: Uint8Array, off: number): void {
    const w = this.w;
    for (let i = 0; i < 16; i++) {
      const j = off + i * 4;
      w[i] = (b[j]! << 24) | (b[j + 1]! << 16) | (b[j + 2]! << 8) | b[j + 3]!;
    }
    for (let i = 16; i < 64; i++) {
      const x = w[i - 15]!;
      const y = w[i - 2]!;
      const s0 = rotr(x, 7) ^ rotr(x, 18) ^ (x >>> 3);
      const s1 = rotr(y, 17) ^ rotr(y, 19) ^ (y >>> 10);
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) | 0;
    }
    const h = this.h;
    let a = h[0]!, bb = h[1]!, c = h[2]!, d = h[3]!, e = h[4]!, f = h[5]!, g = h[6]!, hh = h[7]!;
    for (let i = 0; i < 64; i++) {
      const t1 = (hh + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + K[i]! + w[i]!) | 0;
      const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & bb) ^ (a & c) ^ (bb & c))) | 0;
      hh = g; g = f; f = e; e = (d + t1) | 0; d = c; c = bb; bb = a; a = (t1 + t2) | 0;
    }
    h[0] = h[0]! + a; h[1] = h[1]! + bb; h[2] = h[2]! + c; h[3] = h[3]! + d;
    h[4] = h[4]! + e; h[5] = h[5]! + f; h[6] = h[6]! + g; h[7] = h[7]! + hh;
  }
}

export function createSha256(): Sha256Hasher {
  return new Sha256();
}

/** One-shot SHA-256 of a string (as UTF-8) or of bytes. */
export function sha256(input: string | Uint8Array): string {
  return createSha256().update(typeof input === 'string' ? utf8Bytes(input) : input).digest();
}
